<script setup lang="ts">
import axios from 'axios'
import { Loader2, Search, ExternalLink } from 'lucide-vue-next'
import type { DeliverooBranch, StoreOption } from '@/types/reviewPlatforms'

const props = defineProps<{
  isOpen: boolean
  availableStores: StoreOption[]
  isSaving: boolean
  hasCredentials: boolean
  excludeBranchIds: string[]
}>()

const emit = defineEmits<{
  close: []
  save: [
    payload: {
      email?: string
      password?: string
      orgId: string
      branches: { branchId: string; storeId: number }[]
    }
  ]
}>()

const { t } = useI18n()
const { user } = useLocalStorage()

type Step = 'login' | 'discovering' | 'mapping'

const step = ref<Step>('login')
const email = ref('')
const password = ref('')
const discoverError = ref('')
const discoveredBranches = ref<DeliverooBranch[]>([])
const branchStoreMap = ref<Record<string, number | null>>({})

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      email.value = ''
      password.value = ''
      discoverError.value = ''
      discoveredBranches.value = []
      branchStoreMap.value = {}
      step.value = props.hasCredentials ? 'discovering' : 'login'
      if (props.hasCredentials) rediscover()
    }
  }
)

const storeOptions = computed(() => props.availableStores.map((s) => ({ value: s.id, label: s.name })))

const newBranches = computed(() => discoveredBranches.value.filter((b) => !props.excludeBranchIds.includes(b.branchId)))

const mappedCount = computed(() => Object.values(branchStoreMap.value).filter((v) => v !== null).length)

async function discover() {
  discoverError.value = ''
  step.value = 'discovering'
  try {
    const { data } = await axios.post('/review-managers/deliveroo/discover', {
      email: email.value,
      password: password.value
    })
    handleDiscoverResult(data.data?.branches ?? [])
  } catch {
    discoverError.value = t('pages.reviewPlatforms.configuration.deliveroo.discoverError')
    step.value = 'login'
  }
}

async function rediscover() {
  discoverError.value = ''
  try {
    const { data } = await axios.post(`/merchants/${user.value.merchant.id}/review-managers/deliveroo/rediscover`)
    handleDiscoverResult(data.data?.branches ?? [])
  } catch {
    discoverError.value = t('pages.reviewPlatforms.configuration.deliveroo.discoverError')
    emit('close')
  }
}

function handleDiscoverResult(branches: DeliverooBranch[]) {
  discoveredBranches.value = branches
  branchStoreMap.value = Object.fromEntries(branches.map((b) => [b.branchId, null]))
  step.value = 'mapping'
}

function onSave() {
  const mapped = newBranches.value.filter((b) => branchStoreMap.value[b.branchId] !== null)
  if (mapped.length === 0) return
  emit('save', {
    email: props.hasCredentials ? undefined : email.value,
    password: props.hasCredentials ? undefined : password.value,
    orgId: mapped[0].orgId,
    branches: mapped.map((b) => ({ branchId: b.branchId, storeId: branchStoreMap.value[b.branchId] as number }))
  })
}
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    :heading-title="t('pages.reviewPlatforms.configuration.deliveroo.linkTitle')"
    size="2xl"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4 px-6 py-4">
      <template v-if="step === 'login'">
        <p class="text-sm text-muted-foreground">{{ t('pages.reviewPlatforms.configuration.deliveroo.loginIntro') }}</p>
        <AppFormInput
          id="deliveroo-link-email"
          v-model="email"
          type="text"
          :label="t('pages.reviewPlatforms.configuration.deliveroo.email')"
          full-width
        />
        <AppFormInput
          id="deliveroo-link-password"
          v-model="password"
          type="password"
          :label="t('pages.reviewPlatforms.configuration.deliveroo.password')"
          full-width
        />
        <Alert v-if="discoverError" variant="destructive">
          <AlertDescription>{{ discoverError }}</AlertDescription>
        </Alert>
        <div class="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" @click="emit('close')">
            {{ t('pages.reviewPlatforms.configuration.shared.cancel') }}
          </Button>
          <Button variant="default-primary" size="sm" :disabled="!email || !password" @click="discover">
            <Search />
            {{ t('pages.reviewPlatforms.configuration.deliveroo.discover') }}
          </Button>
        </div>
      </template>

      <template v-else-if="step === 'discovering'">
        <div class="flex items-center justify-center gap-3 py-12 text-sm text-muted-foreground">
          <Loader2 class="size-4 animate-spin" />
          {{ t('pages.reviewPlatforms.configuration.deliveroo.discovering') }}
        </div>
      </template>

      <template v-else>
        <p
          v-if="newBranches.length === 0"
          class="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground"
        >
          {{ t('pages.reviewPlatforms.configuration.deliveroo.noNewBranches') }}
        </p>

        <template v-else>
          <div class="flex items-center justify-between">
            <p class="text-sm text-muted-foreground">
              {{ t('pages.reviewPlatforms.configuration.deliveroo.assignStoreIntro') }}
            </p>
            <BaseChip
              color="slate"
              :text="
                t('pages.reviewPlatforms.configuration.deliveroo.mappedCount', {
                  count: mappedCount,
                  total: newBranches.length
                })
              "
            />
          </div>

          <div class="space-y-2">
            <div
              v-for="branch in newBranches"
              :key="branch.branchId"
              class="flex flex-col gap-3 rounded-lg border border-border bg-muted/40 p-4 sm:flex-row sm:items-center"
            >
              <div class="min-w-0 flex-1">
                <p class="text-sm font-semibold text-foreground">{{ branch.name }}</p>
                <p v-if="branch.city" class="text-xs text-muted-foreground">{{ branch.city }}</p>
                <Button v-if="branch.customerFacingLink" variant="link" size="sm" class="h-auto px-0 text-xs" as-child>
                  <a :href="branch.customerFacingLink" target="_blank" rel="noopener noreferrer">
                    <ExternalLink class="size-3" />
                    {{ t('pages.reviewPlatforms.configuration.deliveroo.viewBranch') }}
                  </a>
                </Button>
              </div>
              <div class="w-full sm:w-56">
                <ComboboxSelect
                  :model-value="branchStoreMap[branch.branchId] ?? undefined"
                  :options="storeOptions"
                  value-key="value"
                  label-key="label"
                  :placeholder="t('pages.reviewPlatforms.configuration.shared.selectStore')"
                  @update:model-value="
                    (val) => (branchStoreMap[branch.branchId] = typeof val === 'number' ? val : null)
                  "
                />
              </div>
            </div>
          </div>
        </template>

        <div class="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" :disabled="isSaving" @click="emit('close')">
            {{ t('pages.reviewPlatforms.configuration.shared.cancel') }}
          </Button>
          <Button variant="default-primary" size="sm" :disabled="isSaving || mappedCount === 0" @click="onSave">
            <Loader2 v-if="isSaving" class="animate-spin" />
            {{ t('pages.reviewPlatforms.configuration.shared.validate') }}
          </Button>
        </div>
      </template>
    </div>
  </BaseModal>
</template>
